<template>
  <div class="space-y-2">
    <AM3InputSelect
      ref="inputRef"
      v-model="internalModel"
      :label="label"
      :placeholder="placeholder"
      :disabled="disabled"
      :required="required"
      :options="mergedOptions"
      option-label="name"
      option-value="name"
      :multiple="multiple"
      :validate="selectValidate"
      :validate-on-blur="validateOnBlur"
      :allow-create="allowCreate"
      enable-create-event
      :data-test-id="dataTestId"
      :fetch-options="handleFetchOptions"
      :page-size="pageSize"
      :loading="isLoading"
      :expands="expands"
      :show-selection-indicators="false"
      @search="handleSearch"
      @create="handleCreate"
    >
      <template #option="{ option }">
        <div class="flex items-center justify-between w-full gap-3 min-w-0">
          <span class="truncate">{{ option.name }}</span>
          <div class="flex items-center gap-2 shrink-0">
            <AM3Tag
              v-if="option.childTagCount > 0"
              :label="`${option.childTagCount} tags`"
              type="brand"
            />
            <AM3UserCountTag :count="option.totalCount?.count ?? option.count" />
          </div>
        </div>
      </template>
    </AM3InputSelect>

    <div
      v-if="showFavourite && allowCreate && latestSearchText && !hasExactMatch"
      class="flex items-center gap-2"
    >
      <div class="size-8 shrink-0">
        <FavoriteButton
          v-model="newTag.favourite"
          data-test-id="new-tag-favourite"
        />
      </div>
      <span class="text-sm text-secondary">Mark new tag as favourite</span>
    </div>
  </div>
</template>

<script setup lang="ts" generic="M extends TagOption">
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import AM3InputSelect from '@/components/am3-generic/inputs/entry/AM3InputSelect.vue'
import AM3UserCountTag from '@/components/am3-generic/base/AM3UserCountTag.vue'
import AM3Tag from '@/components/am3-generic/base/AM3Tag.vue'
import FavoriteButton from '@/pages/audience/components/FavoriteButton.vue'

interface FetchParams {
  searchString?: string
  searchValue?: string | string[]
  offset?: number
  limit?: number
  purpose: string
}

const selected = defineModel<number[]>()

const emit = defineEmits<{
  create: [tagName: string]
  search: [searchText: string]
  // Emitted when an ADD_TAG request starts; resolved promise is passed so
  // parent Save handlers can await completion without relying on template refs.
  creating: [pending: Promise<void>]
  // Emitted whenever handleFetchOptions resolves, so a parent rendering its
  // own list of the same tags (e.g. a checkbox list) can sync from this
  // component's own fetch instead of issuing a duplicate FETCH_TAGS for the
  // same search text.
  optionsFetched: [options: M[]]
}>()

const props = withDefaults(defineProps<{
  options?: M[]
  label?: string | false
  placeholder?: string
  disabled?: boolean
  multiple?: boolean
  required?: boolean
  allowCreate?: boolean
  showFavourite?: boolean
  dataTestId?: string
  validate?: ((value: number[]) => boolean | string) | null
  validateOnBlur?: boolean
  onCreateTag?: ((tagName: string, favourite: boolean) => Promise<M | null>) | null
  pageSize?: number
  expands?: boolean
}>(), {
  options: () => [],
  label: '',
  placeholder: 'Add tags...',
  disabled: false,
  multiple: true,
  required: false,
  allowCreate: true,
  showFavourite: false,
  dataTestId: 'tags',
  validate: null,
  validateOnBlur: false,
  onCreateTag: null,
  pageSize: 100,
  expands: false,
})

const store = useStore()
const inputRef = ref<InstanceType<typeof AM3InputSelect> | null>(null)
const latestSearchText = ref('')
const fetchedOptions = ref<M[]>([])
// Monotonic guard for handleFetchOptions: it fires on every keystroke with no
// debounce, so an earlier, slower call can resolve after a later, faster one.
// Without this, optionsFetched can emit stale results out of order, letting a
// consumer (e.g. EditFanTagsModal's checkbox list) silently regress to a
// pre-creation state right after a tag is created — see EC-4417.
let fetchSequence = 0
const newTag = ref({ favourite: false })
const createError = ref<string | boolean>(false)
const isLoading = ref(false)
// Tracks an in-flight tag-creation promise so createPending() can await it
// without triggering a duplicate ADD_TAG request.
let pendingCreatePromise: Promise<void> | null = null

const normalizeTagName = (tagName: string) => tagName.trim().toLowerCase()

const mergeUniqueOptions = (...collections: Array<M[] | undefined>) => {
  const map = new Map<string, M>()

  for (const collection of collections) {
    for (const option of collection ?? []) {
      map.set(normalizeTagName(option.name), option)
    }
  }

  return [...map.values()]
}

const optionsByName = computed(() => {
  const map = new Map<string, M>()

  for (const option of props.options) {
    map.set(normalizeTagName(option.name), option)
  }

  for (const option of fetchedOptions.value) {
    map.set(normalizeTagName(option.name), option)
  }

  return map
})

const optionsByOid = computed(() => {
  const map = new Map<number, M>()
  for (const option of props.options) {
    if (option.oid != null) map.set(option.oid, option)
  }
  for (const option of fetchedOptions.value) {
    if (option.oid != null) map.set(option.oid, option)
  }
  return map
})

// childTagCount is derived once here (not per template access/render) so the
// option row can just read it - a plain field, not a recomputed expression.
const mergedOptions = computed(() => [...optionsByName.value.values()].map(option => ({
  ...option,
  childTagCount: option.children?.length ?? 0,
})))

const internalModel = computed<string[] | string | null>({
  get() {
    const tagNames = (selected.value ?? [])
      .map(oid => optionsByOid.value.get(oid)?.name)
      .filter((name): name is string => name !== undefined)

    if (props.multiple) {
      return tagNames
    }

    return tagNames[0] ?? null
  },
  set(value) {
    const tagNames = Array.isArray(value)
      ? value
      : value
        ? [value]
        : []

    const oids = tagNames
      .map(tagName => optionsByName.value.get(normalizeTagName(tagName))?.oid)
      .filter((oid): oid is number => oid !== undefined)
    selected.value = oids
  },
})

const hasExactMatch = computed(() => {
  return optionsByName.value.has(normalizeTagName(latestSearchText.value))
})

const selectValidate = (value: string[] | string | null) => {
  if (createError.value) {
    return createError.value
  }

  if (!props.validate) {
    return true
  }

  const tagNames = Array.isArray(value)
    ? value
    : value
      ? [value]
      : []

  return props.validate(tagNames
    .map(tagName => optionsByName.value.get(normalizeTagName(tagName))?.oid)
    .filter((oid): oid is number => oid !== undefined),
  )
}

function handleSearch(searchText: string) {
  latestSearchText.value = searchText
  if (createError.value) {
    createError.value = false
    resetValidation()
  }
  emit('search', searchText)
}

async function handleFetchOptions(params: FetchParams) {
  const requestId = ++fetchSequence
  try {
    isLoading.value = true

    const response = await store.dispatch('tagModule/FETCH_TAGS', {
      top: params.limit ?? props.pageSize,
      skip: params.offset ?? 0,
      searchString: params.searchString?.trim() || null,
      reload: ['initial-load', 'search', 'restore-base'].includes(params.purpose),
      $orderby: [['sysCtime', 'desc']],
    })

    let rows = (response?.rows || response || []) as M[]
    const shouldResolveMissingSelected = ['initial-load', 'reconciliation-failed', 'restore-base'].includes(
      params.purpose,
    )

    if (shouldResolveMissingSelected && Array.isArray(selected.value) && selected.value.length > 0) {
      const fetchedOids = new Set(rows.map(tag => tag.oid))
      const missingSelectedOids = selected.value.filter(oid => !fetchedOids.has(oid))

      if (missingSelectedOids.length > 0) {
        const selectedResponse = await store.dispatch('tagModule/FETCH_TAGS', {
          top: missingSelectedOids.length,
          skip: 0,
          oids: missingSelectedOids,
          reload: false,
          $orderby: [['sysCtime', 'desc']],
        })
        const selectedRows = (selectedResponse?.rows || selectedResponse || []) as M[]
        rows = mergeUniqueOptions(selectedRows, rows)
      }
    }

    const shouldPreserveSelected = [
      'initial-load',
      'search',
      'restore-base',
      'reconciliation-failed',
    ].includes(params.purpose)
    const selectedTagObjects = (selected.value ?? [])
      .map(oid => optionsByOid.value.get(oid))
      .filter((tag): tag is M => tag !== undefined)

    const result = shouldPreserveSelected
      ? mergeUniqueOptions(selectedTagObjects, rows)
      : rows

    // Only the latest in-flight call gets to update state or emit — a slower,
    // superseded call finishing after a newer one would otherwise merge stale
    // rows back into fetchedOptions (this component's own dropdown) and report
    // stale results to consumers.
    if (requestId === fetchSequence) {
      fetchedOptions.value = mergeUniqueOptions(fetchedOptions.value, rows)
      emit('optionsFetched', result)
    }
    return result
  }
  catch (error) {
    console.error('Error fetching tags:', error)
    return []
  }
  finally {
    isLoading.value = false
  }
}

async function handleCreate(tagName = latestSearchText.value) {
  const normalizedTagName = tagName.trim()

  if (!normalizedTagName || !props.allowCreate || hasExactMatch.value) {
    return
  }

  createError.value = false
  emit('create', normalizedTagName)

  const doCreate = async () => {
    try {
      const createdTag: M | null = props.onCreateTag
        ? await props.onCreateTag(normalizedTagName, newTag.value.favourite)
        : await store.dispatch('tagModule/ADD_TAG', { name: normalizedTagName, favourite: newTag.value.favourite })

      if (createdTag && createdTag.oid != null) {
        fetchedOptions.value = [createdTag, ...fetchedOptions.value]
        selected.value = props.multiple
          ? [...(selected.value ?? []), createdTag.oid]
          : [createdTag.oid]
      }
    }
    catch (error) {
      console.error('Error creating tag:', error)
      createError.value = 'This tag name is already in use. Please enter a different name'
      inputRef.value?.runValidation(internalModel.value, props.required)
      return
    }

    latestSearchText.value = ''
    newTag.value.favourite = false
  }

  pendingCreatePromise = doCreate()
  // Notify parent so it can await this promise without needing a template ref
  // (template refs inside named slots can be null on first render in some builds).
  emit('creating', pendingCreatePromise)
  await pendingCreatePromise
  pendingCreatePromise = null
}

const runValidation = (value = internalModel.value, checkRequired = props.required) => {
  return inputRef.value?.runValidation(value, checkRequired)
}

const resetValidation = () => {
  inputRef.value?.resetValidation()
}

// Awaits any in-flight tag creation without issuing a second ADD_TAG request.
// Called by parent Save handlers to ensure tagIds is fully populated before
// reading settings (race: user clicked Create then Save before ADD_TAG resolved).
const flushPendingCreate = () => pendingCreatePromise ?? Promise.resolve()

defineExpose({
  runValidation,
  resetValidation,
  createPending: flushPendingCreate,
})
</script>

<script lang="ts">
export interface TagOption {
  name: string
  favourite?: boolean
  oid?: number
  count?: number
  // Aggregate count including constituent/child tags (see server's
  // promoter_tag_total_count view). Prefer this over `count`, which is the
  // tag's own direct tally and under-counts for tag groups.
  totalCount?: { oid: number, count: number }
  // Constituent tags grouped under this one. A non-empty array means this
  // option is a tag group, not a plain top-level tag.
  children?: { oid: number, name: string, count: number }[]
}
</script>
